import React, { useState, useEffect } from 'react';
import {
  Plus, Flag, Trash2, Edit3, Save, X, Loader2, Percent, Users, Tag,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const formatApiError = (e, fallback = 'Something went wrong') => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail);
  }
  return fallback;
};

const EMPTY_FLAG = {
  key: '',
  enabled: false,
  rollout_percentage: 0,
  target_user_ids: [],
  description: '',
  category: 'general',
};

const CATEGORY_COLORS = {
  ui: 'bg-blue-900/40 text-blue-200',
  ai: 'bg-purple-900/40 text-purple-200',
  payments: 'bg-emerald-900/40 text-emerald-200',
  general: 'bg-slate-700/40 text-slate-200',
};

const FlagEditor = ({ flag, onChange, isEditing }) => (
  <div className="space-y-4">
    {isEditing ? (
      <div className="flex items-center gap-2 text-sm">
        <Tag className="h-4 w-4 text-slate-400" />
        <code className="text-slate-200 bg-[#0F1730] px-2 py-1 rounded">{flag.key}</code>
      </div>
    ) : (
      <div>
        <label htmlFor="flag-key" className="text-sm font-medium block mb-1.5">Key *</label>
        <Input
          id="flag-key"
          placeholder="e.g. new_overview_design"
          value={flag.key}
          onChange={(e) => onChange({ ...flag, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
          data-testid="flag-key-input"
        />
        <p className="text-xs text-slate-500 mt-1">Lowercase letters, digits, underscores. 2-64 chars.</p>
      </div>
    )}

    <div>
      <label htmlFor="flag-description" className="text-sm font-medium block mb-1.5">Description</label>
      <Textarea
        id="flag-description"
        placeholder="What does this flag control?"
        value={flag.description}
        onChange={(e) => onChange({ ...flag, description: e.target.value })}
        rows={2}
        data-testid="flag-description-input"
      />
    </div>

    <div>
      <label htmlFor="flag-category" className="text-sm font-medium block mb-1.5">Category</label>
      <Input
        id="flag-category"
        placeholder="ui / ai / payments / general"
        value={flag.category}
        onChange={(e) => onChange({ ...flag, category: e.target.value.toLowerCase() })}
        data-testid="flag-category-input"
      />
    </div>

    <div className="flex items-center justify-between p-3 bg-[#0F1730] rounded-md">
      <div>
        <div className="font-medium text-slate-100 text-sm">Enabled</div>
        <p className="text-xs text-slate-400">Master kill-switch. When off, no users get this flag.</p>
      </div>
      <Switch
        checked={flag.enabled}
        onCheckedChange={(v) => onChange({ ...flag, enabled: v })}
        data-testid="flag-enabled-switch"
      />
    </div>

    <div>
      <div className="flex items-center justify-between mb-2">
        <label htmlFor="flag-rollout" className="text-sm font-medium flex items-center gap-1.5">
          <Percent className="h-3.5 w-3.5" /> Rollout percentage
        </label>
        <span className="text-sm font-mono text-red-400" data-testid="flag-rollout-value">
          {flag.rollout_percentage}%
        </span>
      </div>
      <Slider
        id="flag-rollout"
        min={0}
        max={100}
        step={1}
        value={[flag.rollout_percentage]}
        onValueChange={([v]) => onChange({ ...flag, rollout_percentage: v })}
        disabled={!flag.enabled}
        data-testid="flag-rollout-slider"
      />
      <p className="text-xs text-slate-500 mt-1">
        Stable per-user bucket. Each user always lands in the same bucket for a given flag.
      </p>
    </div>

    <div>
      <label htmlFor="flag-targets" className="text-sm font-medium block mb-1.5 flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5" /> Target user IDs (always-on)
      </label>
      <Textarea
        id="flag-targets"
        placeholder="Comma- or newline-separated user IDs"
        value={(flag.target_user_ids || []).join(', ')}
        onChange={(e) => onChange({
          ...flag,
          target_user_ids: e.target.value
            .split(/[\s,]+/)
            .map((s) => s.trim())
            .filter(Boolean),
        })}
        rows={2}
        data-testid="flag-targets-input"
      />
      <p className="text-xs text-slate-500 mt-1">
        These users always get this flag ON regardless of rollout percentage.
      </p>
    </div>
  </div>
);

const FlagRow = ({ flag, onEdit, onDelete, onQuickToggle, busy }) => {
  const catClass = CATEGORY_COLORS[flag.category] || CATEGORY_COLORS.general;
  return (
    <div
      className="flex items-start gap-4 p-4 rounded-lg border border-[#2A3658] bg-[#0F1730]/50 hover:bg-[#0F1730] transition-colors"
      data-testid="feature-flag-row"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <code className="text-slate-100 font-semibold text-sm bg-[#0F1730] px-2 py-0.5 rounded">
            {flag.key}
          </code>
          <Badge className={catClass}>{flag.category}</Badge>
          {flag.enabled ? (
            <Badge className="bg-emerald-900/40 text-emerald-200">{flag.rollout_percentage}% rollout</Badge>
          ) : (
            <Badge className="bg-slate-700/40 text-slate-300">OFF</Badge>
          )}
          {flag.target_user_ids?.length > 0 && (
            <Badge variant="outline" className="text-slate-300">
              {flag.target_user_ids.length} pinned
            </Badge>
          )}
        </div>
        {flag.description && (
          <p className="text-sm text-slate-400 mb-1 line-clamp-2">{flag.description}</p>
        )}
        <p className="text-xs text-slate-500">
          Updated {flag.updated_at ? new Date(flag.updated_at).toLocaleString() : '—'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Switch
          checked={flag.enabled}
          onCheckedChange={(v) => onQuickToggle(flag.key, v)}
          disabled={busy}
          data-testid="flag-row-toggle"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(flag)}
          className="h-8"
          data-testid="flag-row-edit"
        >
          <Edit3 className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(flag.key)}
          className="h-8 text-amber-400 hover:bg-amber-950/40 border-amber-800/40"
          data-testid="flag-row-delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

const FeatureFlagsPage = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingMode, setEditingMode] = useState('create'); // 'create' | 'edit'

  const fetchFlags = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/feature-flags');
      setFlags(r.data?.flags || []);
    } catch (e) {
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlags();
  }, []);

  const openCreate = () => {
    setEditing({ ...EMPTY_FLAG });
    setEditingMode('create');
    setEditorOpen(true);
  };

  const openEdit = (flag) => {
    setEditing({ ...flag });
    setEditingMode('edit');
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.key || editing.key.length < 2) {
      toast.error('Key must be at least 2 characters');
      return;
    }

    try {
      setBusy(true);
      if (editingMode === 'edit') {
        await api.patch(`/admin/feature-flags/${editing.key}`, {
          enabled: editing.enabled,
          rollout_percentage: editing.rollout_percentage,
          target_user_ids: editing.target_user_ids,
          description: editing.description,
          category: editing.category,
        });
        toast.success(`Flag "${editing.key}" updated`);
      } else {
        await api.post('/admin/feature-flags', editing);
        toast.success(`Flag "${editing.key}" created`);
      }
      setEditorOpen(false);
      setEditing(null);
      await fetchFlags();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save flag'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (key) => {
    if (!confirm(`Delete feature flag "${key}"? This cannot be undone.`)) return;
    try {
      setBusy(true);
      await api.delete(`/admin/feature-flags/${key}`);
      toast.success(`Flag "${key}" deleted`);
      await fetchFlags();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to delete flag'));
    } finally {
      setBusy(false);
    }
  };

  const handleQuickToggle = async (key, enabled) => {
    try {
      setBusy(true);
      await api.patch(`/admin/feature-flags/${key}`, { enabled });
      setFlags((prev) => prev.map((f) => (f.key === key ? { ...f, enabled } : f)));
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to toggle'));
      await fetchFlags();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-feature-flags-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Flag className="h-6 w-6 text-red-400" /> Feature Flags
          </h1>
          <p className="text-gray-400">Gate features behind kill-switches, percentage rollouts, and allow-lists.</p>
        </div>
        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openCreate}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
              data-testid="create-flag-button"
            >
              <Plus className="mr-2 h-4 w-4" /> New Flag
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingMode === 'edit' ? 'Edit Feature Flag' : 'Create Feature Flag'}
              </DialogTitle>
              <DialogDescription>
                Gate features behind a kill-switch, percentage rollout, or explicit user allow-list.
              </DialogDescription>
            </DialogHeader>
            {editing && (
              <FlagEditor
                flag={editing}
                onChange={setEditing}
                isEditing={editingMode === 'edit'}
              />
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={busy}>
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>
              <Button onClick={handleSave} disabled={busy} data-testid="flag-save-button">
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Flag
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-white">All Flags ({flags.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading flags...
            </div>
          )}
          {!loading && flags.length === 0 && (
            <div className="text-center py-12" data-testid="feature-flags-empty">
              <Flag className="h-10 w-10 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-300 mb-1 font-medium">No feature flags yet</p>
              <p className="text-slate-500 text-sm mb-4">
                Create your first flag to gate a feature behind a kill-switch or percentage rollout.
              </p>
              <Button onClick={openCreate} className="bg-[#DC2626] hover:bg-[#B91C1C]">
                <Plus className="mr-2 h-4 w-4" /> Create First Flag
              </Button>
            </div>
          )}
          {!loading && flags.length > 0 && (
            <div className="space-y-3" data-testid="feature-flags-list">
              {flags.map((f) => (
                <FlagRow
                  key={f.key}
                  flag={f}
                  busy={busy}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onQuickToggle={handleQuickToggle}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeatureFlagsPage;
